import { MenuGroup, MenuItem, Spinner, Stack, Text, useToast } from "@chakra-ui/react";
import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useApiError } from "../errors/useApiError";
import { BffDownloadError, fetchBffBlob, fetchBffFile, jsonPost, saveBlob } from "./downloadBffFile";
import { isTooLargeToDownload, type ListSelection, type exportRequest } from "./listExport";

export type ListFormat = "XLSX" | "CSV" | "PDF";

export const LIST_FORMATS: ListFormat[] = ["XLSX", "CSV", "PDF"];

export const fileNameFor = (base: string, format: ListFormat): string => `${base}.${format.toLowerCase()}`;

export const listSelection = (t: TFunction, count: number, filters: string[]): ListSelection =>
  filters.length > 0
    ? {
        selection: String(t("{{count}} rows, sorted as on screen", { count })),
        filteredOn: String(t("Filtered on: {{filters}}", { filters: filters.join(" · ") })),
      }
    : { selection: String(t("All {{count}} rows, sorted as on screen", { count })) };

const formatLabel = (t: TFunction, format: ListFormat): string =>
  ({ XLSX: t("Excel (.xlsx)"), CSV: t("CSV (.csv)"), PDF: t("PDF (.pdf)") })[format];

type ListSource =
  | { buildBody: (format: ListFormat) => Promise<ReturnType<typeof exportRequest>>; filters?: never }
  | { filters: Record<string, string>; buildBody?: never };

export const ListDownloadItems = ({
  label,
  endpoint,
  baseFileName,
  errorMessage,
  isLoading,
  isDisabled,
  hint,
  pdfHint,
  onDone,
  ...source
}: {
  label: string;
  endpoint: string;
  baseFileName: string;
  errorMessage: string | ((error: unknown, format: ListFormat) => string);
  isLoading: boolean;
  isDisabled: boolean;
  hint?: string;
  pdfHint?: string;
  onDone: () => void;
} & ListSource) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const toast = useToast();
  const [exporting, setExporting] = useState<ListFormat | null>(null);

  const download = async (format: ListFormat) => {
    setExporting(format);
    try {
      const fileName = fileNameFor(baseFileName, format);
      if (source.filters) {
        const query = new URLSearchParams({ format, ...source.filters });
        const file = await fetchBffFile(`${endpoint}?${query.toString()}`, { method: "POST" });
        saveBlob(file.blob, file.fileName ?? fileName);
      } else {
        const body = { ...(await source.buildBody(format)), fileName };
        saveBlob(await fetchBffBlob(`${endpoint}?format=${format}`, jsonPost(body)), fileName);
      }
    } catch (error) {
      const description = typeof errorMessage === "string" ? errorMessage : errorMessage(error, format);
      if (isTooLargeToDownload(error)) {
        const serverMessage =
          typeof errorMessage === "string" && error instanceof BffDownloadError && error.errorCode
            ? String(t(error.errorCode))
            : undefined;
        const id = `list-download-too-large:${endpoint}:${format}`;
        if (!toast.isActive(id)) {
          toast({ id, title: serverMessage ?? description, status: "warning", isClosable: true });
        }
      } else {
        trigger(error, description);
      }
    } finally {
      setExporting(null);
      onDone();
    }
  };

  return (
    <MenuGroup title={label}>
      {hint && (
        <Text px="3" pb="1" fontSize="xs" color="smMuted">
          {hint}
        </Text>
      )}
      {LIST_FORMATS.map((format, index) => {
        const pdfBlocked = format === "PDF" && pdfHint !== undefined;
        return (
          <MenuItem
            key={format}
            isDisabled={isDisabled || pdfBlocked || exporting !== null}
            onClick={() => void download(format)}
          >
            <Stack spacing="0">
              <Text>
                {formatLabel(t, format)}{" "}
                {(exporting === format || (isLoading && index === 0)) && <Spinner size="xs" ml="1" />}
              </Text>
              {pdfBlocked && !isDisabled && (
                <Text fontSize="xs" color="smMuted">
                  {pdfHint}
                </Text>
              )}
            </Stack>
          </MenuItem>
        );
      })}
    </MenuGroup>
  );
};
